// useEscapeGuestBootstrap.js
// Explicit LOCAL-TESTING FALLBACK ONLY. Establishes an Escape session from
// staging-guest-link-style URL parameters (sessionId/organizationId/userId)
// for direct query-string testing when you're not driving the real guest
// login flow — see
// documentation/escape-to-inner-peace-guest-bootstrap-implementation.md.
//
// The primary, preferred path is now: real guest/login (see
// Frontend/src/pages/loginSlice.js) -> existing sessionStorage/localStorage
// writes -> Escape route. This hook is a no-op whenever sessionId/
// organizationId/userId aren't in the URL, so it never competes with that
// primary flow — it only activates when someone deliberately puts guest
// identity values in the query string themselves. Kept (not removed)
// because it's still useful for testing Escape in isolation without going
// through a full login.
//
// Writes the exact same localStorage.session / sessionStorage.userData
// shape the platform's own login flow produces (sessionSlice.setSession +
// loginSlice's userData), so every existing Escape hook (useEscapePersistence,
// useEscapeReplayStatus) keeps working completely unchanged.
//
// gameId/inviteId may be present in the URL but are intentionally never
// read into storage — gameId routing is platform-owned, and inviteId isn't
// used by Escape today.
//
// Rendering is gated on the returned `ready` flag: useEscapeReplayStatus
// captures sessionStorage.userData via a useRef lazy-initializer on first
// mount, so children must not mount until this bootstrap has already
// written storage — otherwise they'd capture an empty session and never
// re-check it.
import { useLayoutEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSession } from '../../../admin/sessionSlice';

const USER_DATA_KEY = 'userData';
const GUEST_PARAM_KEYS = ['sessionId', 'organizationId', 'userId', 'gameId', 'inviteId'];

export function useEscapeGuestBootstrap() {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('sessionId');
    const organizationId = params.get('organizationId');
    const userId = params.get('userId');

    if (sessionId && organizationId && userId) {
      dispatch(setSession({ sessionId, organizationId }));
      sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ userId }));

      // These are guest credentials, not shareable page state — drop them
      // from the visible URL/history now that they're in storage. Only the
      // known guest keys are removed; any other query params are kept.
      const remaining = new URLSearchParams(window.location.search);
      GUEST_PARAM_KEYS.forEach((key) => remaining.delete(key));
      const query = remaining.toString();
      const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState(null, '', cleanUrl);
    }

    setReady(true);
  }, [dispatch]);

  return ready;
}
